import React from "react";
import { _cs } from "@togglecorp/fujs";

import Button from "../../Action/Button";
import HashManager from "../../General/HashManager";
import List from "../List";
import { addClassName } from "../../../utils/common";

import styles from "./styles.module.scss";

export interface RendererProps {
  className?: string;
  isActive?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
  href?: string;
  [key: string]: unknown; // extra props from rendererParams
}

export interface ScrollTabsProps {
  active?: string | number;
  children?: React.ReactNode;
  className?: string;
  blankClassName?: string;
  itemClassName?: string;
  activeClassName?: string;
  defaultHash?: string;
  onClick?: (key: string, e: React.MouseEvent) => void;
  replaceHistory?: boolean;
  tabs: Record<string, string>;
  useHash?: boolean;

  renderer?: React.ComponentType<RendererProps>;
  rendererClassName?: string;
  rendererParams?: (key: string, value: string, index: number) => RendererProps;

  inverted?: boolean;
  disabled?: boolean;
  disabledTabs?: string[];
  showBeforeTabs?: boolean;

  onHashChange?: (hash: string | undefined) => void;
}

export default class ScrollTabs extends React.Component<
  ScrollTabsProps,
  { hash?: string }
> {
  private tabsContainerRef = React.createRef<HTMLDivElement>();
  private mainContainerRef = React.createRef<HTMLDivElement>();

  static defaultProps: Partial<ScrollTabsProps> = {
    active: undefined,
    children: null,
    className: "",
    blankClassName: undefined,
    itemClassName: "",
    activeClassName: "",
    defaultHash: undefined,
    onClick: () => {},
    replaceHistory: false,
    tabs: {},
    disabledTabs: [],
    useHash: false,
    renderer: undefined,
    rendererClassName: "",
    rendererParams: undefined,
    disabled: false,
    inverted: false,
    showBeforeTabs: false,
    onHashChange: undefined,
  };

  constructor(props: ScrollTabsProps) {
    super(props);
    this.state = { hash: undefined };
  }

  componentDidMount() {
    const { current: tabsContainer } = this.tabsContainerRef;
    const { current: mainContainer } = this.mainContainerRef;

    if (
      tabsContainer &&
      mainContainer &&
      tabsContainer.scrollWidth > tabsContainer.clientWidth
    ) {
      addClassName(mainContainer, styles.scroll);
    }
  }

  private getClassName = () => {
    const { className, inverted } = this.props;
    const parts = [className, "scroll-tabs", styles.scrollTabs];
    if (inverted) {
      parts.push("inverted", styles.inverted);
    }
    return parts.join(" ");
  };

  private getTabClassName = ({
    isActive,
    isBasic,
    isDisabled,
  }: {
    isActive: boolean;
    isBasic: boolean;
    isDisabled: boolean;
  }) => {
    const { itemClassName, activeClassName } = this.props;
    return _cs(
      itemClassName,
      styles.tab,
      "scroll-tab",
      isBasic && styles.basicTab,
      !isBasic && styles.tab,
      isActive && styles.active,
      isActive && "active",
      isActive && activeClassName,
      isDisabled && styles.disabledLink
    );
  };

  private handleHashChange = (hash: string | undefined) => {
    this.setState({ hash });
    this.props.onHashChange?.(hash);
  };

  private handleTabClick = (key: string) => (e: React.MouseEvent) => {
    const { onClick, useHash, replaceHistory } = this.props;
    if (useHash && replaceHistory) {
      window.location.replace(`#/${key}`);
      e.preventDefault();
    }
    onClick?.(key, e);
  };

  private handleLeftButtonClick = () => {
    const { current: tabsContainer } = this.tabsContainerRef;
    if (tabsContainer) tabsContainer.scrollLeft -= 48;
  };

  private handleRightButtonClick = () => {
    const { current: tabsContainer } = this.tabsContainerRef;
    if (tabsContainer) tabsContainer.scrollLeft += 48;
  };

  private renderTab = (_: unknown, key: string, index: number) => {
    const {
      active,
      tabs,
      useHash,
      renderer: Renderer,
      rendererClassName,
      rendererParams,
      disabled,
      disabledTabs = [],
    } = this.props;

    if (!tabs[key]) return null;

    const extraProps = rendererParams
      ? rendererParams(key, tabs[key], index)
      : undefined;

    const onClick = this.handleTabClick(key);
    const isTabDisabled = disabled || disabledTabs.includes(key);

    const isActive = useHash ? this.state.hash === key : String(active) === key;

    if (Renderer) {
      const className = this.getTabClassName({
        isActive,
        isBasic: true,
        isDisabled: isTabDisabled,
      });

      return (
        <Renderer
          key={key}
          className={_cs(rendererClassName, className)}
          isActive={isActive}
          onClick={onClick}
          disabled={isTabDisabled}
          href={useHash ? `#/${key}` : undefined}
          {...extraProps}
        />
      );
    }

    const className = this.getTabClassName({
      isActive,
      isBasic: false,
      isDisabled: isTabDisabled,
    });

    if (isTabDisabled) {
      return (
        <div key={key} className={className}>
          {tabs[key]}
        </div>
      );
    }

    return (
      <a
        key={key}
        className={className}
        onClick={onClick}
        href={useHash ? `#/${key}` : undefined}
      >
        {tabs[key]}
      </a>
    );
  };

  render() {
    const {
      tabs,
      useHash,
      defaultHash,
      showBeforeTabs,
      children,
      blankClassName,
    } = this.props;

    const tabList = Object.keys(tabs);
    const className = this.getClassName();
    return (
      <div ref={this.mainContainerRef} className={className}>
        <HashManager
          tabs={tabs}
          useHash={useHash}
          defaultHash={defaultHash}
          onHashChange={this.handleHashChange}
        />
        <Button
          iconName="chevronLeft"
          transparent
          className={styles.leftButton}
          onClick={this.handleLeftButtonClick}
        />
        <div ref={this.tabsContainerRef} className={styles.tabsContainer}>
          {showBeforeTabs && <div className={styles.nonBlank}>{children}</div>}
          <List data={tabList} modifier={this.renderTab} />
          <div className={_cs(styles.blank, blankClassName)}>
            {!showBeforeTabs && children}
          </div>
        </div>
        <Button
          iconName="chevronRight"
          transparent
          className={styles.rightButton}
          onClick={this.handleRightButtonClick}
        />
      </div>
    );
  }
}
